import React, { useState, useMemo } from "react";
import { Rocket, ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useCheckoutCart } from "../hooks/use-checkout-cart";
import { formatCredit } from "@/features/orders/utils/format-price";
import { CartItem } from "@/features/cart/store/cart-store";
import { useCheckout } from "../hooks/use-checkout";
import { useCheckoutStore } from "../store/checkout-store";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { InstallmentOptions } from "../components/InstallmentOptions";
import { useAgreementsStore } from "@/features/agreements";
import { MerchantApprovalModal } from "../components/MerchantApprovalModal";
import { CheckoutCartItem } from "../components/CheckoutCartItem";
import { MultiVendorPaymentNotice } from "../components/MultiVendorPaymentNotice";

export const OrderSummarySection: React.FC = () => {
  const { t } = useTranslation();
  const { vendorGroups, subtotal, formatPrice, isEmpty, items, shipping, cashback } =
    useCheckoutCart();
  const { totalPrice, completeOrder, isProcessing } = useCheckout();
  const {
    selectedDeliveryAddressId,
    isAddingNewAddress,
    isEditingDeliveryAddress,
    deliveryAddressFormStatus,
    requestAddressFormFocus,
  } = useCheckoutStore();
  const { openViewModal } = useAgreementsStore();

  // Only show items the user has selected in the cart.
  // (buy-now items have isSelected === undefined → treated as selected)
  const selectedItems = useMemo(
    () => items.filter((item) => item.isSelected !== false),
    [items],
  );

  // Group items by vendor for display (same as CartDropdown)
  const groupedItems = useMemo(() => {
    if (!vendorGroups || vendorGroups.length <= 1) {
      return null; // Single vendor or no grouping needed
    }

    return vendorGroups
      .map((group) => ({
        ...group,
        // Map vendor group items to CartItem format using selected items array
        cartItems: selectedItems.filter(
          (item) => item.vendor?.id === group.vendorId,
        ),
      }))
      // Drop vendors that have no selected items
      .filter((group) => group.cartItems.length > 0);
  }, [vendorGroups, selectedItems]);

  // Safe formatPrice with NaN handling
  const safeFormatPrice = (price: number | undefined | null) => {
    if (typeof price !== "number" || isNaN(price)) {
      return formatPrice(0);
    }
    return formatPrice(price);
  };

  const [discountCode, setDiscountCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleApplyDiscount = () => {
    // TODO: Implement discount code validation with backend
    console.log("Applying discount:", discountCode);
  };

  const isDeliveryFormOpen = isAddingNewAddress || isEditingDeliveryAddress;
  const needsAddressCompletion =
    isDeliveryFormOpen && deliveryAddressFormStatus !== "valid";
  const hasNoAddress = !selectedDeliveryAddressId && !isDeliveryFormOpen;

  const handleCompleteOrder = () => {
    if (needsAddressCompletion || hasNoAddress) {
      requestAddressFormFocus();
      return;
    }
    void completeOrder();
  };

  if (isEmpty) {
    return null;
  }

  return (
    <div className="space-y-0">
      {/* Product List by Vendor */}
      <div className="border border-[#e3e8ef] dark:border-gray-700 rounded-t-[12px] overflow-hidden">
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {groupedItems && groupedItems.length > 1 ? (
            groupedItems.map((group, index) => {
              const isLastGroup = index === groupedItems.length - 1;

              // Get merchant info from first item (raw backend item)
              const firstItem = group.items?.[0];

              // Extract base URL from product URL (e.g., https://www.pazarama.com)
              const getMerchantBaseUrl = (productUrl?: string) => {
                if (!productUrl) return undefined;
                try {
                  const url = new URL(productUrl);
                  return url.origin; // Returns: https://www.pazarama.com
                } catch {
                  return undefined;
                }
              };

              const merchantUrl = getMerchantBaseUrl(firstItem?.offer?.url);

              return (
                <div key={group.vendorId} className="space-y-3">
                  {/* Vendor Badge - Clickable External Link */}
                  <a
                    href={merchantUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!merchantUrl) {
                        e.preventDefault();
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-1.5 py-0.5 bg-[#fafafa] dark:bg-gray-800 border border-[#e9eaeb] dark:border-gray-700 rounded-md w-fit hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    {group.vendorLogo && (
                      <img
                        src={group.vendorLogo}
                        alt={group.vendorName}
                        className="w-4 h-4 object-contain"
                      />
                    )}
                    <span className="text-[10px] md:text-xs lg:text-[12px] font-medium text-[#364152] dark:text-gray-300">
                      {group.vendorName}
                    </span>
                    <ArrowUpRight className="w-3 h-3 text-[#364152] dark:text-gray-400" />
                  </a>

                  {group.cartItems.map((item: CartItem) => (
                    <CheckoutCartItem
                      key={item._id}
                      item={item}
                      formatPrice={safeFormatPrice}
                    />
                  ))}

                  {/* Vendor Subtotal */}
                  <div className="flex justify-between items-center pt-2 border-t border-[#e3e8ef] dark:border-gray-700">
                    <span className="text-xs md:text-sm lg:text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                      {t("checkout.subtotal")}
                    </span>
                    <span className="text-xs md:text-sm lg:text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                      {formatPrice(group.subtotal || 0)}
                    </span>
                  </div>
                  {/* Vendor Shipping */}
                  <div className="flex justify-between items-center">
                    <span
                      className={`text-xs md:text-sm lg:text-[14px] font-medium ${
                        group.shipping === 0
                          ? "text-[#17b26a]"
                          : "text-[#121926] dark:text-gray-100"
                      }`}
                    >
                      {group.shipping === 0
                        ? t("cart.freeShipping")
                        : t("checkout.shipping")}
                    </span>
                    <span
                      className={`text-xs md:text-sm lg:text-[14px] font-medium ${
                        group.shipping === 0
                          ? "text-[#17b26a]"
                          : "text-[#121926] dark:text-gray-100"
                      }`}
                    >
                      {formatPrice(group.shipping || 0)}
                    </span>
                  </div>
                  {/* Vendor Total */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
                      {t("checkout.total")}
                    </span>
                    <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
                      {formatPrice(group.total || 0)}
                    </span>
                  </div>

                  {/* Divider between vendors */}
                  {!isLastGroup && (
                    <div className="h-px bg-[#e3e8ef] dark:bg-gray-700 my-4" />
                  )}
                </div>
              );
            })
          ) : (
            // Single vendor or no grouping - show all selected items
            <div className="space-y-3">
              {selectedItems.map((item) => (
                <CheckoutCartItem
                  key={item._id}
                  item={item}
                  formatPrice={safeFormatPrice}
                />
              ))}

              {/* Shipping for single vendor */}
              {(() => {
                const singleVendorGroup =
                  vendorGroups?.find(
                    (g) => g.vendorId === selectedItems[0]?.vendor?.id,
                  ) || vendorGroups?.[0];
                const singleVendorShipping =
                  singleVendorGroup?.shipping || shipping || 0;
                const singleVendorSubtotal =
                  singleVendorGroup?.subtotal || subtotal || 0;
                const singleVendorTotal =
                  singleVendorGroup?.total || totalPrice || 0;

                return (
                  <>
                    <div className="flex justify-between items-center">
                      <span
                        className={`text-xs md:text-sm lg:text-[14px] font-medium ${
                          singleVendorShipping === 0
                            ? "text-[#17b26a]"
                            : "text-[#121926] dark:text-gray-100"
                        }`}
                      >
                        {singleVendorShipping === 0
                          ? t("cart.freeShipping")
                          : t("checkout.shipping")}
                      </span>
                      <span
                        className={`text-xs md:text-sm lg:text-[14px] font-medium ${
                          singleVendorShipping === 0
                            ? "text-[#17b26a]"
                            : "text-[#121926] dark:text-gray-100"
                        }`}
                      >
                        {formatPrice(singleVendorShipping)}
                      </span>
                    </div>

                    {/* Vendor Subtotal */}
                    <div className="flex justify-between items-center pt-2 border-t border-[#e3e8ef] dark:border-gray-700">
                      <span className="text-xs md:text-sm lg:text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                        {t("checkout.subtotal")}
                      </span>
                      <span className="text-xs md:text-sm lg:text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                        {formatPrice(singleVendorSubtotal)}
                      </span>
                    </div>

                    {/* Vendor Total */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
                        {t("checkout.total")}
                      </span>
                      <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
                        {formatPrice(singleVendorTotal)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Discount Code Section */}
      <div className="bg-[#fcfcfd] dark:bg-gray-800 border-x border-[#e3e8ef] dark:border-gray-700 p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-end">
          <div className="flex-1 space-y-1 w-full md:w-auto">
            <Label
              htmlFor="discountCode"
              className="text-xs md:text-sm lg:text-[14px] font-medium text-[#121926] dark:text-gray-100"
            >
              {t("checkout.discountCode")}
            </Label>
            <Input
              id="discountCode"
              placeholder=""
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              className="h-9 md:h-10 rounded-[10px] border-[#e3e8ef] dark:border-gray-700 text-xs md:text-sm lg:text-[14px]"
            />
          </div>
          <Button
            onClick={handleApplyDiscount}
            disabled={!discountCode.trim()}
            variant="darkPrimary"
            className="w-full md:w-auto"
          >
            <Rocket className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span className="text-xs md:text-sm lg:text-[14px] font-semibold">
              {t("common.apply")}
            </span>
            <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </Button>
        </div>
      </div>

      {/* Total Section */}
      <div className="border border-[#e3e8ef] dark:border-gray-700 rounded-b-[12px] p-4 md:p-6 flex flex-col gap-4">
        {/* Price Breakdown + Total + Cashback */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
              {t("checkout.subtotal")}
            </span>
            <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
              {formatPrice(subtotal)}
            </span>
          </div>
          {shipping === 0 ? (
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#17b26a]">
                {t("cart.freeShipping")}
              </span>
              <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#17b26a]">
                {formatPrice(0)}
              </span>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
                {t("checkout.shipping")}
              </span>
              <span className="text-sm md:text-base lg:text-[16px] font-semibold text-[#121926] dark:text-gray-100">
                {formatPrice(shipping)}
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-[#e3e8ef] dark:bg-gray-700 !mt-3" />

          {/* Total */}
          <div className="flex justify-between items-center !mt-3">
            <span className="text-base md:text-lg lg:text-[20px] font-semibold text-[#121926] dark:text-gray-100">
              {t("checkout.grandTotal")}
            </span>
            <span className="text-base md:text-lg lg:text-[20px] font-semibold text-[#121926] dark:text-gray-100">
              {formatPrice(totalPrice)}
            </span>
          </div>

          {/* Cashback */}
          {cashback > 0 && (
            <div className="flex items-center justify-between gap-2 bg-[#f0fdf4] dark:bg-green-950/30 rounded-lg px-3 py-2.5 !mt-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#17b26a] flex-shrink-0" />
                <span className="text-sm font-medium text-[#17b26a]">
                  {t("cart.cashbackEarn")}
                </span>
              </div>
              <span className="text-sm font-bold text-[#17b26a]">
                +{formatCredit(cashback)}
              </span>
            </div>
          )}
        </div>

        {/* Installment Options */}
        <InstallmentOptions />

        {/* Terms Checkbox */}
        <div className="flex items-start gap-2">
          <Checkbox
            id="terms"
            checked={termsAccepted}
            onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
            className="mt-0.5"
          />
          <Label
            htmlFor="terms"
            className="text-xs md:text-sm lg:text-[14px] font-medium text-[#121926] dark:text-gray-100 cursor-pointer leading-4 md:leading-5"
          >
            {t("checkout.agreeToTermsPrefix")}{" "}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openViewModal("privacy-policy");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  openViewModal("privacy-policy");
                }
              }}
              className="underline font-semibold cursor-pointer hover:text-primary"
            >
              {t("checkout.privacyPolicy")}
            </span>{" "}
            {t("common.and")}{" "}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openViewModal("terms-of-use");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  openViewModal("terms-of-use");
                }
              }}
              className="underline font-semibold cursor-pointer hover:text-primary"
            >
              {t("checkout.terms")}
            </span>
          </Label>
        </div>

        {/* Pay Now Button */}
        <div className="space-y-2">
          <Button
            onClick={handleCompleteOrder}
            disabled={isProcessing || !termsAccepted}
            visuallyDisabled={needsAddressCompletion || hasNoAddress}
            className="w-full py-2.5 md:py-3 text-xs md:text-sm lg:text-base font-semibold"
            variant="darkPrimary"
          >
            {isProcessing ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Rocket className="w-3.5 h-3.5 md:w-4 md:h-4" />
                {t("checkout.payNow")}
                <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </>
            )}
          </Button>
          {(needsAddressCompletion || hasNoAddress) && (
            <p className="text-[11px] md:text-xs text-center text-text-tertiary dark:text-gray-400">
              {t("checkout.completeAddressToContinue")}
            </p>
          )}
          {isDeliveryFormOpen && deliveryAddressFormStatus === "valid" && (
            <p className="text-[11px] md:text-xs text-center text-text-tertiary dark:text-gray-400">
              {t("checkout.addressWillBeSavedOnPay")}
            </p>
          )}
        </div>

        {/* Çok satıcılı sepette 3D ekranı yalnızca ilk tutarı gösterir */}
        <MultiVendorPaymentNotice />
      </div>

      {/* Merchant Approval Modal — zero props, self-contained */}
      <MerchantApprovalModal />
    </div>
  );
};
